import NProgress from 'nprogress';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect } from 'react';
// material
// import { createStyles, makeStyles } from '@mui/styles';
import { alpha, styled } from '@mui/material/styles';
import { Box } from '@mui/material';

// NProgress injects the markup but not the stylesheet. Without this import the
// bar renders unstyled, i.e. invisible.
import 'nprogress/nprogress.css';

import NProgressStyle from './styles';
//
// import {Logo} from '../logo';

// ----------------------------------------------------------------------

// const nprogressStyle = makeStyles((theme) =>
//   createStyles({
//     '@global': {
//       '#nprogress': {
//         pointerEvents: 'none',
//         '& .bar': {
//           top: 0,
//           left: 0,
//           height: 2,
//           width: '100%',
//           position: 'fixed',
//           zIndex: theme.zIndex?.snackbar,
//           backgroundColor: theme.palette?.primary.main,
//           boxShadow: `0 0 2px ${theme.palette?.primary.main}`
//         },
//         '& .peg': {
//           right: 0,
//           opacity: 1,
//           width: 100,
//           height: '100%',
//           display: 'block',
//           position: 'absolute',
//           transform: 'rotate(3deg) translate(0px, -4px)',
//           boxShadow: `0 0 10px ${theme.palette?.primary.main}, 0 0 5px ${theme.palette?.primary.main}`
//         }
//       }
//     }
//   })
// );

const RootStyle = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background?.default
}));

// ----------------------------------------------------------------------

function ProgressBar() {
  // Was `NProgressStyle()`, which called the hook and threw the returned
  // <GlobalStyles> element away, so the bar was never styled at all. Rendered as
  // a component instead so its output actually reaches the document.
  return <NProgressStyle />;
}

export default function LoadingScreen({ ...other }) {
  // The two rotating rings below run forever. Honour a reduced-motion request:
  // this screen is the first thing painted, so its animations compete with
  // everything the page is still trying to load.
  const reduceMotion = useReducedMotion();

  // Was useMemo, which is not a lifecycle hook and gives no cleanup. start/done
  // belong in an effect, and done() has to run on unmount or the bar is left
  // frozen across the app's whole lifetime.
  useEffect(() => {
    NProgress.start();

    return () => NProgress.done();
  }, []);

  return (
    <RootStyle {...other}>
      <ProgressBar />

      <motion.div
        initial={{ rotateY: 0 }}
        animate={reduceMotion ? { rotateY: 0 } : { rotateY: 360 }}
        transition={{
          duration: 2,
          ease: 'easeInOut',
          repeatDelay: 1,
          repeat: reduceMotion ? 0 : Infinity,
        }}
      >
        {/* Fixed to a real asset. This pointed at `/static/brand/logo.png`, which
            is not in public/, so the loading screen showed a broken image. */}
        <Box
          component="img"
          alt="ASAP UAE"
          src="/assets/logos/logo.png"
          // Intrinsic size, so the box is correct before the bytes arrive.
          width={163}
          height={139}
          sx={{ width: 64, height: 64, display: 'block' }}
        />
      </motion.div>

      <Box
        component={motion.div}
        animate={
          reduceMotion
            ? {}
            : {
                scale: [1.2, 1, 1, 1.2, 1.2],
                rotate: [270, 0, 0, 270, 270],
                opacity: [0.25, 1, 1, 1, 0.25],
                borderRadius: ['25%', '25%', '50%', '50%', '25%'],
              }
        }
        transition={{ ease: 'linear', duration: 3.2, repeat: reduceMotion ? 0 : Infinity }}
        sx={{
          width: 100,
          height: 100,
          borderRadius: '25%',
          position: 'absolute',
          border: (theme) => `solid 3px ${alpha(theme.palette.primary.dark, 0.24)}`,
        }}
      />

      <Box
        component={motion.div}
        animate={
          reduceMotion
            ? {}
            : {
                scale: [1, 1.2, 1.2, 1, 1],
                rotate: [0, 270, 270, 0, 0],
                opacity: [1, 0.25, 0.25, 0.25, 1],
                borderRadius: ['25%', '25%', '50%', '50%', '25%'],
              }
        }
        transition={{
          ease: 'linear',
          duration: 3.2,
          repeat: reduceMotion ? 0 : Infinity,
        }}
        sx={{
          width: 120,
          height: 120,
          borderRadius: '25%',
          position: 'absolute',
          border: (theme) => `solid 8px ${alpha(theme.palette.primary.dark, 0.24)}`,
        }}
      />
    </RootStyle>
  );
}
